import {
  AutomationTemplateIds,
  checkIsTeammateCoachingSettings,
} from "@repo/app-types/entities/automation";
import { checkIsManagerGroup } from "@repo/app-types/entities/group";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  isTeammateAnEditor,
  isTeammateAReviewer,
  renderTeammateDisplayName,
} from "@repo/app-types/entities/teammate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { useMemo } from "react";
import { useAllAutomationsQuery } from "../../api/entities/automation.js";
import { useAllTeammateGroupsQuery } from "../../api/entities/group.js";
import { useAllTeammatesQuery } from "../../api/entities/teammate.js";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";
import { usePlaybookAndVoiceAgentOptions } from "../../hooks/usePlaybookAndVoiceAgentOptions.js";
import type { AutomationFieldsContext } from "./automationSettingsFields.js";

/**
 * Loads the playbook and teammate options that the automation configuration form
 * needs, shared by the new-automation page and the detail page's Overview tab.
 */
export function useAutomationFieldsContext(): AutomationFieldsContext & {
  playbookNamesById: ReadonlyMap<PlaybookId, string>;
} {
  const teammateAuthorization = useTeammateAuthorization();
  // Reviewers can open the automation pages but lack UPDATE on automations, so the
  // form renders every field disabled.
  const isReadOnly = !teammateAuthorization.can(
    PermissionActions.UPDATE,
    PermissionSubjects.AUTOMATIONS,
  );

  const {
    options: playbookAndVoiceAgentOptions,
    isLoading: arePlaybooksLoading,
    hasLoadedAllOptions: hasLoadedAllPlaybookOptions,
  } = usePlaybookAndVoiceAgentOptions();
  const { data: automations } = useAllAutomationsQuery();
  const { data: teammates = [], isLoading: areTeammatesLoading } =
    useAllTeammatesQuery();
  const { data: groups = [], isLoading: areGroupsLoading } =
    useAllTeammateGroupsQuery();

  // Playbooks already governed by a coaching automation, so a second coaching
  // automation can't be pointed at them (enforced server-side; surfaced here to
  // disable them in the playbook dropdown).
  const coachingPlaybookIdsInUse = useMemo<readonly PlaybookId[]>(
    () =>
      (automations ?? []).flatMap((automation) =>
        automation.template_id === AutomationTemplateIds.TEAMMATE_COACHING &&
        checkIsTeammateCoachingSettings(automation.settings)
          ? [automation.settings.playbook_id]
          : [],
      ),
    [automations],
  );

  // Includes voice agents (keyed by their backing playbook), so scopes that
  // analyze voice agent calls render the agent's name.
  const playbookNamesById = useMemo(
    () =>
      new Map<PlaybookId, string>(
        playbookAndVoiceAgentOptions.map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [playbookAndVoiceAgentOptions],
  );

  const teammateOptions = useMemo(
    () =>
      teammates.map((teammate) => ({
        id: teammate.id,
        label: renderTeammateDisplayName(teammate),
      })),
    [teammates],
  );

  // Keep the subscriber picker aligned with server-side recipient eligibility.
  const editorAndReviewerTeammateOptions = useMemo(
    () =>
      teammates
        .filter(
          (teammate) =>
            isTeammateAnEditor(teammate) || isTeammateAReviewer(teammate),
        )
        .map((teammate) => ({
          id: teammate.id,
          label: renderTeammateDisplayName(teammate),
        })),
    [teammates],
  );

  const groupOptions = useMemo(
    () =>
      groups.map((group) => ({
        id: group.id,
        label: group.name,
      })),
    [groups],
  );

  const managerTeammateIds = useMemo(
    () =>
      groups.flatMap((group) =>
        checkIsManagerGroup(group) && group.teammate_ids.length > 0
          ? [group.manager_id]
          : [],
      ),
    [groups],
  );

  return {
    isReadOnly,
    playbookAndVoiceAgentOptions,
    arePlaybooksLoading,
    hasLoadedAllPlaybookOptions,
    // Recipient pickers stay disabled until both queries resolve, so they never
    // render an empty menu that would read as "no eligible teammates."
    areRecipientOptionsLoading: areTeammatesLoading || areGroupsLoading,
    teammateOptions,
    editorAndReviewerTeammateOptions,
    groupOptions,
    managerTeammateIds,
    coachingPlaybookIdsInUse,
    playbookNamesById,
  };
}
